/**
 * Company GSTINs — add/manage every GSTIN under the same company.
 * Also lets admins verify each GSTIN live via WhiteBooks GSTNDETAILS.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, RefreshCw, Star, Search, CheckSquare, Square } from "lucide-react";

export default function CompanyGstins() {
  const [items, setItems] = useState([]);
  const [newGstin, setNewGstin] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter-172 · Discover by PAN
  const [pan, setPan] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState([]);   // [{gstin, state_cd, legal_name, status, already_added, checked}]
  const [discoverySource, setDiscoverySource] = useState("");

  const discover = async () => {
    if (pan.length !== 10) { toast.error("PAN must be exactly 10 characters"); return; }
    setDiscovering(true); setDiscovered([]);
    try {
      const r = await api.post("/company/gstins/discover-by-pan", { pan: pan.toUpperCase() });
      const rows = (r.data.gstins || []).map((g) => ({ ...g, checked: !g.already_added }));
      setDiscovered(rows);
      setDiscoverySource(r.data.source || "unknown");
      if (rows.length === 0) {
        toast.warning(r.data.error || "No GSTINs found for this PAN on gst.gov.in");
      } else {
        toast.success(`Found ${rows.length} GSTIN(s) · source: ${r.data.source}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery failed"); }
    setDiscovering(false);
  };

  const toggleRow = (gstin) => setDiscovered((rows) =>
    rows.map((r) => r.gstin === gstin ? { ...r, checked: !r.checked } : r));

  const bulkAdd = async () => {
    const picked = discovered.filter((r) => r.checked && !r.already_added);
    if (picked.length === 0) { toast.error("Nothing selected"); return; }
    setBusy(true);
    try {
      const r = await api.post("/company/gstins/bulk-add", {
        gstins: picked.map((p) => ({ gstin: p.gstin, label: p.state_cd })),
      });
      toast.success(`Added ${r.data.added_count} · Skipped ${r.data.skipped?.length || 0} · Failed ${r.data.failed?.length || 0}`);
      if ((r.data.failed || []).length > 0) {
        console.warn("Bulk-add failures:", r.data.failed);
      }
      setDiscovered([]); setPan("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk-add failed"); }
    setBusy(false);
  };

  const load = () => api.get("/company/gstins").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (newGstin.length !== 15) { toast.error("GSTIN must be 15 characters"); return; }
    setBusy(true);
    try {
      const r = await api.post("/company/gstins",
        { gstin: newGstin.toUpperCase(), label: newLabel, is_default: items.length === 0 });
      setNewGstin(""); setNewLabel("");
      if (r.data?.verified) {
        toast.success(`Verified: ${r.data.legal_name || r.data.gstin}`, {
          description: r.data.address ? r.data.address.slice(0, 120) : undefined,
        });
      } else if (r.data?.verify_error) {
        toast.warning("GSTIN added — verification pending", {
          description: r.data.verify_error.slice(0, 220),
        });
      } else {
        toast.success("GSTIN added");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    setBusy(false);
  };

  const setDefault = async (id) => {
    try { await api.patch(`/company/gstins/${id}`, { is_default: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Remove this GSTIN? Invoices & bills tagged to it will keep their tag.")) return;
    try { await api.delete(`/company/gstins/${id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const verify = async (id) => {
    try {
      const r = await api.post(`/company/gstins/${id}/verify`);
      toast.success(`Verified: ${r.data.legal_name || "OK"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="company-gstins-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-8 h-8 text-emerald-500" />
          Company GSTINs
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Register every GSTIN registered under this company. Users can be restricted
          to specific GSTINs (Users → Access page). Sales & Purchase screens will let
          users pick which GSTIN each invoice is issued under.
        </p>
      </div>

      {/* Iter-172 · Auto-fetch all GSTINs under a PAN */}
      <Card className="border-accent/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-accent" />
            Auto-discover GSTINs by PAN
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Enter the company's PAN. We'll pull every GSTIN registered under it
            from the GST portal (gst.gov.in public search) — you pick which to onboard.
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>PAN (10-digit)</Label>
              <Input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
                        maxLength={10} placeholder="ABCDE1234F"
                        className="font-mono uppercase" data-testid="pan-discover-input" />
            </div>
            <Button onClick={discover} disabled={discovering || pan.length !== 10}
                      className="gap-1.5" data-testid="pan-discover-btn">
              {discovering
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Search className="w-4 h-4" />}
              Fetch GSTINs
            </Button>
          </div>
          {discovered.length > 0 && (
            <div className="border rounded-sm mt-3" data-testid="discovered-list">
              <div className="text-[10px] uppercase tracking-widest font-mono px-3 py-2 bg-muted flex items-center justify-between">
                <span>Discovered · source: {discoverySource} · {discovered.length} GSTIN(s)</span>
                <Button size="sm" onClick={bulkAdd} disabled={busy}
                          className="h-7 text-xs gap-1.5" data-testid="bulk-add-btn">
                  <Plus className="w-3 h-3" /> Add selected
                </Button>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {discovered.map((r) => (
                    <tr key={r.gstin} className="border-t hover:bg-muted/40"
                          data-testid={`discovered-${r.gstin}`}>
                      <td className="py-2 pl-3 w-8">
                        <button onClick={() => toggleRow(r.gstin)}
                                  disabled={r.already_added}
                                  className="disabled:opacity-30">
                          {r.checked
                            ? <CheckSquare className="w-4 h-4 text-accent" />
                            : <Square className="w-4 h-4 text-muted-foreground" />}
                        </button>
                      </td>
                      <td className="py-2 font-mono text-xs">{r.gstin}</td>
                      <td className="text-xs">{r.state_cd}</td>
                      <td className="text-xs text-muted-foreground">{r.legal_name || "—"}</td>
                      <td className="text-xs text-right pr-3">
                        {r.already_added
                          ? <Badge variant="outline">Already added</Badge>
                          : <Badge className="bg-emerald-500">{r.status}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Add a GSTIN manually</CardTitle></CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="flex-1">
            <Label>GSTIN (15-digit)</Label>
            <Input value={newGstin} onChange={(e) => setNewGstin(e.target.value.toUpperCase())}
                     maxLength={15} placeholder="27BVFPB3852E1ZV"
                     className="font-mono uppercase" data-testid="new-gstin" />
          </div>
          <div className="flex-1">
            <Label>Short label (branch / state)</Label>
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                     placeholder="Maharashtra HQ" data-testid="new-label" />
          </div>
          <Button onClick={add} disabled={busy || newGstin.length !== 15} data-testid="btn-add-gstin">
            <Plus className="w-4 h-4 mr-2" />Add
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Registered GSTINs ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No GSTINs added yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2">GSTIN</th>
                  <th className="text-left">Label</th>
                  <th className="text-left">State</th>
                  <th className="text-left">Legal Name</th>
                  <th className="text-left">Status</th>
                  <th className="text-right pr-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((g) => (
                  <tr key={g.id} className="border-b" data-testid={`gstin-row-${g.gstin}`}>
                    <td className="py-2 font-mono text-xs">{g.gstin}</td>
                    <td>{g.label}</td>
                    <td>{g.state_cd}</td>
                    <td className="text-xs text-muted-foreground">{g.legal_name || "—"}</td>
                    <td>
                      {g.is_default && <Badge className="mr-1"><Star className="w-3 h-3" /></Badge>}
                      {g.verified
                        ? <Badge className="bg-emerald-500">Verified</Badge>
                        : <Badge variant="outline">Unverified</Badge>}
                    </td>
                    <td className="text-right space-x-1 pr-2">
                      <Button size="sm" variant="outline" onClick={() => verify(g.id)}
                                title="Verify on gst.gov.in">
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                      {!g.is_default && (
                        <Button size="sm" variant="outline" onClick={() => setDefault(g.id)}
                                  title="Set as default">
                          <Star className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => del(g.id)}
                                title="Remove">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
